import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { TaskFilterOptions } from 'shared/types/filter';
import { TaskType } from 'shared/types/task-base';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import { useRepeatingTasks } from '../use-repeating-tasks';
import { processScheduleTasks } from './process-schedule-tasks';

export const useFilteredRepeatingTasks = (
  filters: TaskFilterOptions | TaskFilterOptions[],
) => {
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const repeatingTasks = useRepeatingTasks();
  const activeRepeatingTasks = useMemo(
    () => repeatingTasks.filter((task) => !task.archivedAt),
    [repeatingTasks],
  );

  return useMemo(() => {
    const filtersArray = Array.isArray(filters) ? filters : [filters];

    const hasDeadlineFilters = filtersArray.every(
      (f) =>
        f.deadlineDeadlineStartDate ||
        f.deadlineDeadlineEndDate ||
        f.hasDeadlineDeadline,
    );

    if (hasDeadlineFilters) {
      return [];
    }

    // Check if ALL filter sets exclude repeating-tasks. If even one allows repeating-tasks, we proceed.
    const allowsRepeating = filtersArray.some(
      (f) => !f.types?.length || f.types.includes(TaskType.Repeating),
    );

    if (!allowsRepeating) {
      return [];
    }

    return processScheduleTasks(activeRepeatingTasks, filters, {
      weekStartsOn,
      today,
    });
  }, [filters, activeRepeatingTasks, today, weekStartsOn]);
};
